import { create } from 'zustand';
import { supabase } from '../supabase';
import { SampleRequest, PieceScanLog, Profile, SystemConfig } from '../types';
import { useAuthStore } from './useAuthStore';
import { useOfflineQueue } from './useOfflineQueue';

interface PieceState {
    samples: SampleRequest[];
    tailors: Profile[];
    defectsConfig: SystemConfig | null;
    loading: boolean;
    error: string | null;
    scanHistory: PieceScanLog[];
    recentLogs: PieceScanLog[];

    fetchAssignedSamples: () => Promise<void>;
    fetchRecentLogs: () => Promise<void>;
    fetchTailors: () => Promise<void>;
    fetchScanHistory: (pieceId: string) => Promise<void>;
    fetchPieceDetails: (pieceId: string) => Promise<any>;
    fetchSystemConfig: () => Promise<void>;
    findPieceByQrCode: (qrCode: string) => Promise<{ id: string; sample_request_id: string } | null>;
    updateSystemConfig: (items: Record<string, string[]>) => Promise<void>;
}

// usePieceStore is now a READ-ONLY store for fetching data
// All WRITE operations (cutting, sewing, QC) are handled by dedicated stores:
// - useCuttingStore (uses rpc_start_cutting, rpc_end_cutting)
// - useSewingStore (uses rpc_assign_tailors_bulk, rpc_complete_sewing_bulk)
// - useInlineQCStore (uses rpc_inline_qc_bulk)
// - useFinalQCStore (uses rpc_final_qc_bulk)

export const usePieceStore = create<PieceState>((set, get) => ({
    samples: [],
    tailors: [],
    defectsConfig: null,
    loading: false,
    error: null,
    scanHistory: [],
    recentLogs: [],

    // ─── READS ────────────────────────────────────────────────────────────

    fetchRecentLogs: async () => {
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id) return;
        const { data } = await supabase.from('piece_scan_log').select('*')
            .eq('factory_id', u.factory_id).order('scanned_at', { ascending: false }).limit(10);
        set({ recentLogs: data || [] });
    },

    fetchAssignedSamples: async () => {
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id || !navigator.onLine) return;
        const hasCachedData = get().samples.length > 0;
        if (!hasCachedData) set({ loading: true });
        set({ error: null });

        try {
            let query = supabase
                .from('sample_production')
                .select(`
                    *,
                    sample_requests!inner(
                        id, factory_id, style_id, sample_type, request_date, deadline_date, status,
                        version_number, requested_qty, is_fasttrack, file_handover_done, file_handover_at,
                        planned_pattern_due, planned_file_handover, planned_submission,
                        buyer_submitted_date, buyer_approval_date, buyer_decision, remarks,
                        styles(
                            id, factory_id, style_code, style_name, buyer_name, category, image_url
                        )
                    ),
                    sample_pieces(
                        id, sample_production_id, sample_request_id, piece_number, size, status,
                        created_at, last_action_at, current_tailor_id, current_tailor_name, qr_code, sewing_cycles
                    ),
                    sample_planning!left(
                        cutting_start_at, cutting_end_at, is_active
                    )
                `)
                .eq('factory_id', u.factory_id);

            if (u.role === 'supervisor') {
                query = query
                    .in('status', ['Cutting Started', 'Cutting Completed', 'Sewing Started', 'Sewing Completed', 'QC Ongoing', 'Sample Completed']);
            }

            const { data: prods, error } = await query;
            if (error) throw error;
            if (!prods || prods.length === 0) { set({ samples: [], loading: false }); return; }

            const mapped: SampleRequest[] = prods
                .map(prod => {
                    const req = prod.sample_requests;
                    if (!req) return null;
                    
                    const planning = Array.isArray(prod.sample_planning) 
                        ? prod.sample_planning.find((p: any) => p.is_active === true) 
                        : (prod.sample_planning?.is_active === true ? prod.sample_planning : null);

                    return {
                        ...req,
                        styles: req.styles || null,
                        sample_pieces: prod.sample_pieces || [],
                        sample_production: {
                            id: prod.id,
                            factory_id: prod.factory_id,
                            sample_request_id: prod.sample_request_id,
                            status: prod.status,
                            file_received_at: prod.file_received_at,
                            fabric_cuttable_width: prod.fabric_cuttable_width,
                            special_instructions: prod.special_instructions,
                            job_card_config: prod.job_card_config,
                            size_breakdown: prod.size_breakdown,
                            fabric_issued: prod.fabric_issued,
                            fabric_leftover: prod.fabric_leftover,
                            trims_issued: prod.trims_issued,
                            cutting_leftover_qty: prod.cutting_leftover_qty,
                            buyer_rework: prod.buyer_rework,
                            created_at: prod.created_at,
                            updated_at: prod.updated_at,
                        },
                        sample_planning: planning || null,
                    };
                })
                .filter((s): s is SampleRequest => s !== null && !!s.id)
                .sort((a, b) => new Date(a.deadline_date).getTime() - new Date(b.deadline_date).getTime());

            set({ samples: mapped });
        } catch (err: any) {
            console.error('[FETCH] ❌', err.message);
            set({ error: err.message });
        } finally { set({ loading: false }); }
    },

    fetchTailors: async () => {
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id) return;
        const { data } = await supabase.from('tailors').select('*')
            .eq('factory_id', u.factory_id).eq('is_active', true).order('name');
        set({
            tailors: (data || []).map((t: any) => ({
                id: t.id, factory_id: t.factory_id, role: 'tailor' as const,
                full_name: t.name, email: '', employee_id: t.employee_id,
            })) as any[],
        });
    },

    fetchScanHistory: async (pieceId) => {
        const { data } = await supabase.from('piece_scan_log').select('*')
            .eq('sample_piece_id', pieceId).order('scanned_at', { ascending: true });
        set({ scanHistory: data || [] });
    },

    fetchPieceDetails: async (pieceId) => {
        const { data, error } = await supabase.from('sample_pieces')
            .select('*, sewing_cycles, inline_qc_attempts, final_qc_attempts')
            .eq('id', pieceId)
            .single();
        if (error) throw error;
        return data;
    },

    fetchSystemConfig: async () => {
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id) return;
        const { data } = await supabase.from('system_config').select('*')
            .eq('factory_id', u.factory_id).eq('category', 'defects').maybeSingle();
        if (data?.items) {
            const items = typeof data.items === 'string' ? JSON.parse(data.items) : data.items;
            set({ defectsConfig: { ...data, items } });
        }
    },

    findPieceByQrCode: async (qrCode) => {
        const { data } = await supabase.from('sample_pieces')
            .select('id, sample_request_id').eq('qr_code', qrCode).maybeSingle();
        return data || null;
    },

    // ─── WRITE OPERATIONS REMOVED ─────────────────────────────────────────
    // All write operations now handled by dedicated stores:
    // - useCuttingStore for cutting operations
    // - useSewingStore for sewing operations  
    // - useInlineQCStore for inline QC
    // - useFinalQCStore for final QC
    // This keeps usePieceStore as a pure data-fetching store

    // Deprecated - use useCuttingStore.startCutting instead
    startCutting: async (sampleId, startDate) => {
        throw new Error('Use useCuttingStore.startCutting instead');
    },

    // Deprecated - use useCuttingStore.endCutting instead
    endCutting: async (sampleId, endDate, remarks, leftoverFabric) => {
        throw new Error('Use useCuttingStore.endCutting instead');
    },

    // Deprecated - use useSewingStore.assignPieceToTailor instead
    assignPieceToTailor: async (_sampleId, pieceId, tailorId, tailorName, startDate) => {
        throw new Error('Use useSewingStore.assignPieceToTailor instead');
    },

    // Deprecated - use useSewingStore.completePieceSewing instead
    completePieceSewing: async (_sampleId, pieceId, endDate) => {
        console.error('⚠️ usePieceStore.completePieceSewing is deprecated. Use useSewingStore.completePieceSewing instead.');
        throw new Error('Use useSewingStore.completePieceSewing instead');
    },

    // Deprecated - use useInlineQCStore.internalQCPiece instead
    internalQCPiece: async (_sampleId, pieceId, passed, defects, notes, date) => {
        console.error('⚠️ usePieceStore.internalQCPiece is deprecated. Use useInlineQCStore.internalQCPiece instead.');
        throw new Error('Use useInlineQCStore.internalQCPiece instead');
    },

    // Deprecated - use useFinalQCStore.qcInspectorPiece instead
    qcInspectorPiece: async (_sampleId, pieceId, passed, defects, notes, date) => {
        console.error('⚠️ usePieceStore.qcInspectorPiece is deprecated. Use useFinalQCStore.qcInspectorPiece instead.');
        throw new Error('Use useFinalQCStore.qcInspectorPiece instead');
    },

    // ─── CONFIG ───────────────────────────────────────────────────────────

    updateSystemConfig: async (items) => {
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id) return;
        await supabase.from('system_config').upsert({
            factory_id: u.factory_id, category: 'defects',
            items, updated_at: new Date().toISOString(),
        }, { onConflict: 'factory_id,category' });
        await get().fetchSystemConfig();
    },
}));
